import React, { useState, useCallback } from "react";
import LinkAccount from "../Portfolio/LinkAccount";
import { useAuth } from "../../contexts/AuthContext";
import { createGoal } from "../../api/goalsClient";
import { updatePreferences } from "../../api/profileClient";

const STEPS = [
    { id: "welcome", title: "Welcome to OmniWorth" },
    { id: "link", title: "Connect Your Accounts" },
    { id: "goals", title: "Set Your First Goal" },
    { id: "done", title: "You're All Set" },
];

function StepIndicator({ currentStep }) {
    return (
        <div className="flex items-center justify-center gap-2 mb-8">
            {STEPS.map((step, i) => (
                <div
                    key={step.id}
                    className={`h-1.5 rounded-full transition-all ${
                        i <= currentStep ? "bg-accent-500 w-8" : "bg-gray-200 w-4"
                    }`}
                />
            ))}
        </div>
    );
}

function WelcomeStep({ onNext }) {
    const { user } = useAuth();
    const firstName = user?.displayName?.split(" ")[0] || "there";

    return (
        <div className="text-center max-w-md mx-auto">
            <div className="w-20 h-20 bg-accent-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-3">
                Hey {firstName}, let's build your wealth dashboard
            </h2>
            <p className="text-gray-500 mb-8 leading-relaxed">
                OmniWorth connects to your bank and investment accounts to give you a complete picture
                of your net worth, savings rate, and projected path to financial independence.
            </p>

            <div className="space-y-4 text-left mb-8">
                <OnboardingFeature
                    icon="📊"
                    title="Real-time net worth tracking"
                    desc="See your complete financial picture across all accounts"
                />
                <OnboardingFeature
                    icon="💰"
                    title="Savings rate dashboard"
                    desc="Track how much you save each month with automatic categorization"
                />
                <OnboardingFeature
                    icon="🎯"
                    title="FI number & projections"
                    desc="Know exactly when you'll hit your financial goals"
                />
            </div>

            <button onClick={onNext} className="btn-primary w-full py-3 text-lg font-semibold">
                Get Started
            </button>
        </div>
    );
}

function OnboardingFeature({ icon, title, desc }) {
    return (
        <div className="flex items-start gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <span className="text-xl mt-0.5">{icon}</span>
            <div>
                <p className="text-gray-900 font-medium text-sm">{title}</p>
                <p className="text-gray-500 text-xs">{desc}</p>
            </div>
        </div>
    );
}

function LinkStep({ onNext, onSkip }) {
    const [linked, setLinked] = useState(false);

    const handleLinkSuccess = useCallback(() => {
        setLinked(true);
    }, []);

    return (
        <div className="text-center max-w-md mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Connect Your Accounts</h2>
            <p className="text-gray-500 mb-8">
                Link your bank and investment accounts securely through Plaid.
                Your credentials are never stored on our servers.
            </p>

            <div className="mb-6">
                <LinkAccount onLinkSuccess={handleLinkSuccess} />
            </div>

            {linked && (
                <div className="mb-6 p-3 bg-accent-50 border border-accent-200 rounded-lg">
                    <p className="text-accent-600 text-sm font-medium">Account linked successfully!</p>
                    <p className="text-gray-500 text-xs mt-1">You can link more accounts later from the Accounts page.</p>
                </div>
            )}

            <div className="flex gap-3">
                <button
                    onClick={onSkip}
                    className="flex-1 px-4 py-3 text-gray-500 hover:text-gray-900 transition-colors text-sm"
                >
                    Skip for now
                </button>
                <button
                    onClick={onNext}
                    disabled={!linked}
                    className={`flex-1 py-3 rounded-lg font-semibold transition-all ${
                        linked
                            ? "btn-primary"
                            : "bg-gray-200 text-gray-500 cursor-not-allowed"
                    }`}
                >
                    Continue
                </button>
            </div>
        </div>
    );
}

function GoalsStep({ onNext, onSkip }) {
    const [goal, setGoal] = useState("");
    const [selectedPreset, setSelectedPreset] = useState(null);
    const [monthlyContribution, setMonthlyContribution] = useState("");
    const [timeline, setTimeline] = useState(10);

    const presetGoals = [
        { label: "Financial Independence", amount: 1000000 },
        { label: "First $100K", amount: 100000 },
        { label: "House Down Payment", amount: 60000 },
        { label: "Emergency Fund", amount: 25000 },
    ];

    const handlePresetClick = (preset) => {
        setGoal(preset.amount);
        setSelectedPreset(preset.label);
    };

    const handleContinue = () => {
        const goalName = selectedPreset || "My Goal";
        const targetDate = new Date();
        targetDate.setFullYear(targetDate.getFullYear() + timeline);
        const targetDateStr = targetDate.toISOString().split("T")[0];

        createGoal({
            name: goalName,
            targetAmount: goal,
            currentAmount: 0,
            targetDate: targetDateStr,
            trackType: "NET_WORTH",
            icon: null,
        }).catch((err) => {
            console.error("Failed to save onboarding goal:", err);
        });

        if (monthlyContribution) {
            updatePreferences({ monthlyInvestmentAmount: parseInt(monthlyContribution) }).catch((err) => {
                console.error("Failed to save monthly investment preference:", err);
            });
        }

        onNext({ goal, timeline, monthlyContribution: monthlyContribution || 0 });
    };

    return (
        <div className="text-center max-w-md mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">What's Your Goal?</h2>
            <p className="text-gray-500 mb-6">
                Pick a target amount. We'll track your progress and project when you'll get there.
            </p>

            <div className="grid grid-cols-2 gap-3 mb-6">
                {presetGoals.map((preset) => (
                    <button
                        key={preset.amount}
                        onClick={() => handlePresetClick(preset)}
                        className={`p-3 rounded-lg text-left transition-all ${
                            goal === preset.amount
                                ? "bg-accent-50 border border-accent-200 text-gray-900"
                                : "bg-white border border-gray-200 text-gray-700 hover:border-gray-300"
                        }`}
                    >
                        <p className="text-sm font-medium">{preset.label}</p>
                        <p className="text-xs text-gray-500">${preset.amount.toLocaleString()}</p>
                    </button>
                ))}
            </div>

            <div className="mb-6">
                <label className="text-sm text-gray-500 block mb-2 text-left">Or enter a custom amount</label>
                <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <input
                        type="number"
                        value={goal || ""}
                        onChange={(e) => {
                            setGoal(parseInt(e.target.value) || "");
                            setSelectedPreset(null);
                        }}
                        placeholder="500,000"
                        className="input w-full pl-7"
                    />
                </div>
            </div>

            <div className="mb-6">
                <label className="text-sm text-gray-500 block mb-2 text-left">How much do you invest each month?</label>
                <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <input
                        type="number"
                        value={monthlyContribution}
                        onChange={(e) => setMonthlyContribution(parseInt(e.target.value) || "")}
                        placeholder="1,000"
                        className="input w-full pl-7"
                    />
                </div>
            </div>

            <div className="mb-8">
                <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-500">Timeline</span>
                    <span className="text-gray-900">{timeline} years</span>
                </div>
                <input
                    type="range"
                    min={1}
                    max={40}
                    value={timeline}
                    onChange={(e) => setTimeline(parseInt(e.target.value))}
                    className="w-full accent-accent-500"
                />
            </div>

            <div className="flex gap-3">
                <button
                    onClick={onSkip}
                    className="flex-1 px-4 py-3 text-gray-500 hover:text-gray-900 transition-colors text-sm"
                >
                    Skip for now
                </button>
                <button
                    onClick={handleContinue}
                    disabled={!goal}
                    className={`flex-1 py-3 rounded-lg font-semibold transition-all ${
                        goal
                            ? "btn-primary"
                            : "bg-gray-200 text-gray-500 cursor-not-allowed"
                    }`}
                >
                    Continue
                </button>
            </div>
        </div>
    );
}

function DoneStep({ onFinish }) {
    return (
        <div className="text-center max-w-md mx-auto">
            <div className="w-20 h-20 bg-accent-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M5 13l4 4L19 7" />
                </svg>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-3">You're all set!</h2>
            <p className="text-gray-500 mb-8 leading-relaxed">
                Your accounts are syncing now. Your dashboard will populate with real data
                within a few minutes. In the meantime, explore your projections and set up goals.
            </p>

            <button onClick={onFinish} className="btn-primary w-full py-3 text-lg font-semibold">
                Go to Dashboard
            </button>
        </div>
    );
}

export default function OnboardingFlow({ onComplete }) {
    const [step, setStep] = useState(0);

    const handleNext = (data) => {
        if (step === STEPS.length - 1) {
            onComplete(data);
            return;
        }
        setStep(step + 1);
    };

    const handleSkip = () => {
        if (step === STEPS.length - 2) {
            // Skipping goals step goes to done
            setStep(STEPS.length - 1);
        } else {
            setStep(step + 1);
        }
    };

    return (
        <div className="min-h-[60vh] flex flex-col justify-center py-12">
            <StepIndicator currentStep={step} />
            {step === 0 && <WelcomeStep onNext={handleNext} />}
            {step === 1 && <LinkStep onNext={handleNext} onSkip={handleSkip} />}
            {step === 2 && <GoalsStep onNext={handleNext} onSkip={handleSkip} />}
            {step === 3 && <DoneStep onFinish={() => onComplete()} />}
        </div>
    );
}
